import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { QuizRunner } from "../components/QuizRunner";
import { quizBySlug, quizPath } from "../lib/quiz";
import {
  articleJsonLd,
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  faqJsonLd,
  quizJsonLd,
  quizOgImage,
  socialMeta,
} from "../lib/seo";

/**
 * `/quiz/<slug>` — one quiz.
 *
 * Looked up synchronously from the static corpus, same as an article, and an
 * unknown slug is a 404 rather than a redirect to the hub — see
 * `blog.$slug.tsx` for why that matters.
 *
 * ── The structured data is split by kind ─────────────────────────────────────
 *
 * Every quiz emits `BlogPosting` (dates and publisher — freshness is a real
 * input for this kind of query) and `BreadcrumbList`. Only a `knowledge` quiz
 * additionally emits `Quiz`, because that vocabulary asserts the questions have
 * correct answers and a personality test marked up that way is a false claim.
 */
export const Route = createFileRoute("/quiz/$slug")({
  loader: ({ params }) => {
    const quiz = quizBySlug(params.slug);
    if (!quiz) throw notFound();
    return quiz;
  },
  headers: documentHeaders,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const path = quizPath(loaderData.slug);
    return {
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.description },
        ...socialMeta({
          title: loaderData.title,
          description: loaderData.description,
          path,
          lang: "en",
          image: quizOgImage(loaderData.slug),
          imageAlt: loaderData.h1,
        }),
        {
          "script:ld+json": articleJsonLd({
            headline: loaderData.h1,
            description: loaderData.description,
            path,
            published: loaderData.published,
            updated: loaderData.updated,
          }),
        },
        {
          "script:ld+json": breadcrumbJsonLd([
            { name: "Bible Shield", path: "/" },
            { name: "Quizzes", path: "/quiz" },
            { name: loaderData.h1, path },
          ]),
        },
        ...(loaderData.kind === "knowledge"
          ? [
              {
                "script:ld+json": quizJsonLd({
                  name: loaderData.h1,
                  description: loaderData.description,
                  path,
                  questions: loaderData.questions,
                }),
              },
            ]
          : []),
        // Only when the questions are actually visible on the page, which for
        // every quiz here they are.
        ...(loaderData.faq
          ? [{ "script:ld+json": faqJsonLd(loaderData.faq) }]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
    };
  },
  component: QuizPage,
});

function QuizPage() {
  const quiz = Route.useLoaderData();
  return (
    <PageChrome>
      {/* Same column as the quiz, or it hangs off the left of the measure on
          any screen wider than the text. */}
      <nav
        aria-label="breadcrumb"
        className="mx-auto mb-6 w-full max-w-[68ch] text-sm text-ink2"
      >
        <Link
          to="/quiz"
          className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
        >
          quizzes
        </Link>
      </nav>
      <QuizRunner quiz={quiz} />
    </PageChrome>
  );
}
